import { useCallback, useRef, useMemo, useState } from 'react';
import { Button, Space, Tag, Modal, message, Select, Alert, Tooltip } from 'antd';
import {
  DeleteOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getMobileDevices,
  deactivateMobileDevice,
  syncMobileDevice,
  MOBILE_PLATFORMS,
  type MobileDeviceDocument,
  type MobileDeviceFilters,
} from '@/api/mobileApi';
import { useAuth } from '@/hooks/useAuth';
import MobileNav from './MobileNav';

export default function MobileDeviceListPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [platformFilter, setPlatformFilter] = useState<string | undefined>();
  const [activeFilter, setActiveFilter] = useState<string | undefined>();

  const canRead = hasPermission('mobile', 'read');
  const canWrite = hasPermission('mobile', 'write');
  const canDelete = hasPermission('mobile', 'delete');

  const fetchData = useCallback(
    (params: SearchParams) => {
      const filters: MobileDeviceFilters = { ...params };
      if (platformFilter) filters.platform = platformFilter;
      if (activeFilter) filters.is_active = activeFilter;
      return getMobileDevices(filters);
    },
    [platformFilter, activeFilter]
  );

  const handleDeactivate = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('mobile.confirm_deactivate'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deactivateMobileDevice(id);
            message.success(t('mobile.deactivate_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const handleSync = useCallback(
    async (id: string) => {
      try {
        await syncMobileDevice(id);
        message.success(t('mobile.sync_success'));
        gridRef.current?.reload();
      } catch {
        message.error(t('app.error'));
      }
    },
    [t]
  );

  const columns: ColumnsType<MobileDeviceDocument> = useMemo(
    () => [
      {
        title: t('mobile.field.device_name'),
        dataIndex: 'device_name',
        key: 'device_name',
        ellipsis: true,
        sorter: true,
      },
      {
        title: t('mobile.field.platform'),
        dataIndex: 'platform',
        key: 'platform',
        width: 100,
        render: (v: string) => (
          <Tag color={v === 'ios' ? 'blue' : v === 'android' ? 'green' : 'default'}>
            {t(`mobile.platform_options.${v}`, v)}
          </Tag>
        ),
      },
      {
        title: t('mobile.field.os_version'),
        dataIndex: 'os_version',
        key: 'os_version',
        width: 120,
        ellipsis: true,
      },
      {
        title: t('mobile.field.app_version'),
        dataIndex: 'app_version',
        key: 'app_version',
        width: 110,
      },
      {
        title: t('mobile.field.push_provider'),
        dataIndex: 'push_provider',
        key: 'push_provider',
        width: 120,
        render: (v: string) => t(`mobile.push_provider_options.${v}`, v),
      },
      {
        title: t('mobile.field.is_active'),
        dataIndex: 'is_active',
        key: 'is_active',
        width: 80,
        render: (v: boolean) =>
          v ? (
            <Tooltip title={t('mobile.field.is_active')}>
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
            </Tooltip>
          ) : (
            <CloseCircleOutlined style={{ color: '#999' }} />
          ),
      },
      {
        title: t('mobile.field.last_login_at'),
        dataIndex: 'last_login_at',
        key: 'last_login_at',
        width: 160,
        sorter: true,
        render: (v: string) => (v ? new Date(v).toLocaleString() : '-'),
      },
      {
        title: t('mobile.field.last_sync_at'),
        dataIndex: 'last_sync_at',
        key: 'last_sync_at',
        width: 160,
        sorter: true,
        render: (v: string) => (v ? new Date(v).toLocaleString() : '-'),
      },
      {
        title: t('mobile.field.ip_address'),
        dataIndex: 'ip_address',
        key: 'ip_address',
        width: 130,
        render: (v: string) => v || '-',
      },
      {
        title: t('mobile.field.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        width: 160,
        sorter: true,
        render: (v: string) => (v ? new Date(v).toLocaleString() : '-'),
      },
      {
        title: t('app.actions'),
        key: 'col_actions',
        width: 120,
        render: (_: unknown, record: MobileDeviceDocument) => (
          <Space size="small">
            {canWrite && record.is_active && (
              <Tooltip title={t('mobile.sync')}>
                <Button
                  type="link"
                  size="small"
                  icon={<SyncOutlined />}
                  onClick={() => handleSync(record.id)}
                />
              </Tooltip>
            )}
            {canDelete && record.is_active && (
              <Tooltip title={t('mobile.deactivate')}>
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeactivate(record.id)}
                />
              </Tooltip>
            )}
          </Space>
        ),
      },
    ],
    [t, canWrite, canDelete, handleDeactivate, handleSync]
  );

  const toolbar = (
    <Space wrap>
      <Select
        allowClear
        placeholder={t('mobile.field.platform')}
        value={platformFilter}
        onChange={(v) => {
          setPlatformFilter(v);
          gridRef.current?.reload();
        }}
        style={{ width: 130 }}
        options={MOBILE_PLATFORMS.map((p) => ({
          value: p,
          label: t(`mobile.platform_options.${p}`),
        }))}
      />
      <Select
        allowClear
        placeholder={t('mobile.field.is_active')}
        value={activeFilter}
        onChange={(v) => {
          setActiveFilter(v);
          gridRef.current?.reload();
        }}
        style={{ width: 130 }}
        options={[
          { value: 'true', label: t('app.yes') },
          { value: 'false', label: t('app.no') },
        ]}
      />
    </Space>
  );

  if (!canRead) return <Alert type="error" message={t('app.forbidden')} />;

  return (
    <div>
      <MobileNav />
      <h2>{t('mobile.devices_title')}</h2>
      <DataGrid<MobileDeviceDocument>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('mobile.search_placeholder')}
        defaultSortField="last_login_at"
        defaultSortDescending
        toolbar={toolbar}
      />
    </div>
  );
}
